import React, { useEffect, useState } from 'react';
import annyang from 'annyang';
import { 
  getSocialMediaApps, 
  getSearchPlatforms, 
  searchOnPlatform, 
  playYouTubeVideo, 
  sendWhatsAppMessage,
  logout
} from '../services/AppService';

const VoiceControl = ({ isAuthenticated, onAppOpen, onMessageSend, onLogout }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [voiceStatus, setVoiceStatus] = useState('Voice recognition inactive');
  const [isSpeechSupported, setIsSpeechSupported] = useState(true);
  const [awaitingPlatformChoice, setAwaitingPlatformChoice] = useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = useState('');
  const [awaitingSocialMediaChoice, setAwaitingSocialMediaChoice] = useState(false);

  // Initialize voice control
  useEffect(() => {
    // Check browser support first
    if (!annyang) {
      setIsSpeechSupported(false);
      setVoiceStatus('Speech recognition not supported in this browser. Try Chrome.');
      return;
    }

    // Only initialize voice control if authenticated
    if (!isAuthenticated) {
      setVoiceStatus('Please authenticate first to enable voice control');
      return;
    }

    // Define commands
    const commands = {
      'open *app': (app) => {
        if (app.toLowerCase() === 'social media' || app.toLowerCase() === 'social') {
          setTranscript('Which social media app would you like to open?');
          setVoiceStatus('Waiting for social media app choice...');
          setAwaitingSocialMediaChoice(true);
          
          // Change command to listen for social media choice
          annyang.removeCommands();
          
          // Create commands for each social media app
          const socialCommands = {};
          const socialApps = getSocialMediaApps();
          socialApps.forEach(app => {
            socialCommands[app.name.toLowerCase()] = () => {
              setAwaitingSocialMediaChoice(false);
              setTranscript(`Opening ${app.name}`);
              onAppOpen(app.key);
              
              // Reset to original commands
              setTimeout(() => {
                annyang.removeCommands();
                initializeCommands();
                setVoiceStatus('Listening for commands...');
              }, 2000);
            };
          });
          
          // Add a cancel command
          socialCommands['cancel'] = () => {
            setAwaitingSocialMediaChoice(false);
            setTranscript('Cancelled opening social media');
            
            // Reset to original commands
            setTimeout(() => {
              annyang.removeCommands();
              initializeCommands();
              setVoiceStatus('Listening for commands...');
            }, 1000);
          };
          
          annyang.addCommands(socialCommands);
        } else {
          setTranscript(`Opening ${app}`);
          onAppOpen(app.toLowerCase());
        }
      },
      'send message to *contact': (contact) => {
        setTranscript(`Preparing to send message to ${contact}. What should I say?`);
        setVoiceStatus('Listening for message content...');
        
        // Change command to listen for message content
        annyang.removeCommands();
        annyang.addCommands({
          '*message': (message) => {
            // Process contact for direct handling
            const cleanContact = contact.trim().toLowerCase();
            
            console.log('Send message voice command: ', { contact, cleanContact, message });
            
            // Send message directly
            const result = onMessageSend(contact, message);
            
            // Display the cleaned email if available
            if (result && result.emailAddress) {
              setTranscript(`Sending "${message}" to ${result.emailAddress}`);
            } else {
              setTranscript(`Sending "${message}" to ${contact}`);
            }
            
            // Reset to original commands after sending
            setTimeout(() => {
              annyang.removeCommands();
              initializeCommands();
              setVoiceStatus('Listening for commands...');
            }, 2000);
          }
        });
      },
      'search for *query': (query) => {
        setPendingSearchQuery(query);
        setTranscript(`Where would you like to search for "${query}"?`);
        setVoiceStatus('Waiting for search platform choice...');
        setAwaitingPlatformChoice(true);
        
        // Change command to listen for platform choice
        annyang.removeCommands();
        
        const searchCommands = {};
        const platforms = getSearchPlatforms();
        
        // Add commands for each platform with alternative phrases
        platforms.forEach(platform => {
          // Create several variations for each platform name
          const variations = [
            platform,                      // e.g. "google"
            `on ${platform}`,              // e.g. "on google"
            `using ${platform}`,           // e.g. "using google"
            `search on ${platform}`,       // e.g. "search on google"
            `search using ${platform}`,    // e.g. "search using google"
            `search with ${platform}`,     // e.g. "search with google"
            `use ${platform}`              // e.g. "use google"
          ];
          
          variations.forEach(variation => {
            searchCommands[variation] = () => {
              setAwaitingPlatformChoice(false);
              const result = searchOnPlatform(platform, pendingSearchQuery);
              setTranscript(result.message);
              
              // Reset to original commands
              setTimeout(() => {
                annyang.removeCommands();
                initializeCommands();
                setVoiceStatus('Listening for commands...');
                setPendingSearchQuery('');
              }, 2000);
            };
          });
        });
        
        // Add a direct command for each platform to make it even easier
        searchCommands['google'] = () => {
          setAwaitingPlatformChoice(false);
          const result = searchOnPlatform('google', pendingSearchQuery);
          setTranscript(result.message);
          resetCommands();
        };
        
        searchCommands['youtube'] = () => {
          setAwaitingPlatformChoice(false);
          const result = searchOnPlatform('youtube', pendingSearchQuery);
          setTranscript(result.message);
          resetCommands();
        };
        
        searchCommands['bing'] = () => {
          setAwaitingPlatformChoice(false);
          const result = searchOnPlatform('bing', pendingSearchQuery);
          setTranscript(result.message);
          resetCommands();
        };
        
        // Function to reset commands
        const resetCommands = () => {
          setTimeout(() => {
            annyang.removeCommands();
            initializeCommands();
            setVoiceStatus('Listening for commands...');
            setPendingSearchQuery('');
          }, 2000);
        };
        
        // Add a cancel command
        searchCommands['cancel'] = () => {
          setAwaitingPlatformChoice(false);
          setTranscript('Search cancelled');
          setPendingSearchQuery('');
          
          // Reset to original commands
          setTimeout(() => {
            annyang.removeCommands();
            initializeCommands();
            setVoiceStatus('Listening for commands...');
          }, 1000);
        };
        
        annyang.addCommands(searchCommands);
      },
      'log out': handleLogout,
      'logout': handleLogout,
      'sign out': handleLogout,
      'signout': handleLogout,
      'stop listening': () => {
        setTranscript('Voice commands stopped');
        annyang.abort();
        setIsListening(false);
        setVoiceStatus('Voice recognition paused');
      }
    };

    // Function to initialize commands
    const initializeCommands = () => {
      annyang.removeCommands();
      annyang.addCommands(commands);
    };

    // Reset annyang in case it was previously initialized 
    try {
      annyang.abort();
    } catch (e) {
      console.log('Annyang was not yet running');
    }

    // Initialize
    initializeCommands();

    // Configure annyang with additional settings
    annyang.debug(false); // Set to true to see detailed logs
    annyang.setLanguage('en-US');

    // Add callbacks
    annyang.addCallback('start', () => {
      console.log('Speech recognition started');
      setIsListening(true);
      setVoiceStatus('Listening for commands...');
    });

    annyang.addCallback('resultMatch', (userSaid, commandText) => {
      console.log('User said:', userSaid);
      console.log('Command matched:', commandText);
    });

    annyang.addCallback('resultNoMatch', (phrases) => {
      console.log("I didn't recognize that command. Phrases heard:", phrases);
    });

    annyang.addCallback('error', (error) => {
      console.error('Speech recognition error:', error);
      if (error.error === 'network') {
        setVoiceStatus('Network error. Check your internet connection.');
      } else if (error.error === 'not-allowed') {
        setVoiceStatus('Microphone access denied. Please enable microphone permissions.');
      } else {
        setVoiceStatus('Error with speech recognition. Please try again.');
      }
    });

    // Start listening
    try {
      annyang.start({ autoRestart: true, continuous: true });
    } catch (e) {
      console.error('Error starting annyang:', e);
      setVoiceStatus('Failed to start speech recognition. Try refreshing the page.');
    }

    // Clean up on component unmount
    return () => {
      if (annyang) {
        annyang.abort();
      }
    };
  }, [isAuthenticated, onAppOpen, onMessageSend, onLogout, pendingSearchQuery]);

  // Toggle listening manually
  const toggleListening = () => {
    if (!annyang) {
      setVoiceStatus('Speech recognition not supported in this browser');
      return;
    }

    if (isListening) {
      annyang.abort();
      setIsListening(false);
      setVoiceStatus('Voice recognition paused');
    } else {
      try {
        annyang.start({ autoRestart: true, continuous: true });
        setIsListening(true);
        setVoiceStatus('Listening for commands...');
      } catch (e) {
        console.error('Error toggling speech recognition:', e);
        setVoiceStatus('Failed to start speech recognition. Try refreshing the page.');
      }
    }
  };

  // Define the logout handler function
  function handleLogout() {
    setTranscript('Logging out');
    
    // Update localStorage for cross-component communication
    try {
      localStorage.setItem('isAuthenticated', 'false');
      
      // Create and dispatch a custom event to notify other components
      const logoutEvent = new Event('userLogout');
      window.dispatchEvent(logoutEvent);
    } catch (err) {
      console.error('Error setting auth state in localStorage:', err);
    }
    
    const result = logout();
    setVoiceStatus(result.message);
    
    // Call the onLogout callback to update app state
    if (onLogout) {
      onLogout();
    }
  }

  return (
    <div className="voice-control">
      <h2>Voice Control</h2>
      <p className="status">{voiceStatus}</p>
      {transcript && <p className="transcript">Last command: {transcript}</p>}
      <div className="voice-buttons">
        <button 
          onClick={toggleListening} 
          disabled={!isAuthenticated || !isSpeechSupported}
          className={isListening ? 'listening' : ''}
        >
          {isListening ? 'Stop Listening' : 'Start Listening'}
        </button>
      </div>
      {!isSpeechSupported && (
        <div className="browser-warning">
          <p>Your browser doesn't support speech recognition.</p>
          <p>Please use Google Chrome for the best experience.</p>
        </div>
      )}
      <div className="voice-instructions">
        <h3>Available Voice Commands:</h3>
        <ul>
          <li><strong>"Open [app]"</strong> - Open any of these apps:
            <ul>
              <li>Social Media: WhatsApp, Facebook, Instagram, Twitter, X</li>
              <li>Email: Gmail</li>
              <li>Video: YouTube</li>
              <li>Search: Google</li>
            </ul>
          </li>
          <li><strong>"Open social media"</strong> - This will prompt you to choose from:
            <ul>
              <li>WhatsApp, Facebook, Instagram, Twitter, X</li>
            </ul>
          </li>
          <li><strong>"Send message to [platform] [contact]"</strong> - Specific platforms:
            <ul>
              <li>WhatsApp: "Send message to WhatsApp 8948584874" (number without +91)</li>
              <li>Email: "Send message to email johndoe" (will send to johndoe@gmail.com)</li>
              <li>Email with domain: "Send message to email john@example.com"</li>
              <li>Spaces in voice detection are automatically removed: "Send message to email john doe" → johndoe@gmail.com</li>
            </ul>
          </li>
          <li><strong>"Search for [query]"</strong> - Search for information
            <ul>
              <li>You'll be asked to choose: Google, YouTube, or Bing</li>
              <li>Example: "Search for voice assistants" then say "Google"</li>
            </ul>
          </li>
          <li><strong>Logout Commands:</strong> - Any of these will log you out:
            <ul>
              <li>"Log out", "Logout", "Sign out", "Signout"</li>
            </ul>
          </li>
          <li><strong>"Stop listening"</strong> - Pause voice recognition</li>
        </ul>
      </div>
    </div>
  );
};

export default VoiceControl; 